import React, { useState, useMemo, useCallback } from 'react';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

const COMMON_TOKENS = ['ETH', 'WBTC', 'USDC', 'USDT', 'DAI', 'WETH', 'FRAX', 'ARB', 'OP', 'MATIC', 'LINK', 'UNI', 'AAVE', 'CRV', 'stETH', 'rETH'];

const PRESETS = [
  { label: 'ETH 2x', tokenA: 'ETH', tokenB: 'USDC', priceA: 2000, priceB: 1, futureA: 4000, futureB: 1, weightA: 50 },
  { label: 'ETH -50%', tokenA: 'ETH', tokenB: 'USDC', priceA: 2000, priceB: 1, futureA: 1000, futureB: 1, weightA: 50 },
  { label: 'BTC +30%', tokenA: 'WBTC', tokenB: 'USDC', priceA: 60000, priceB: 1, futureA: 78000, futureB: 1, weightA: 50 },
  { label: 'Stable depeg 5%', tokenA: 'USDC', tokenB: 'USDT', priceA: 1, priceB: 1, futureA: 0.95, futureB: 1, weightA: 50 },
  { label: 'Balancer 80/20', tokenA: 'ETH', tokenB: 'USDC', priceA: 2000, priceB: 1, futureA: 4000, futureB: 1, weightA: 80 },
];

const WEIGHT_PRESETS = [
  { label: '50/50', value: 50 },
  { label: '80/20', value: 80 },
  { label: '60/40', value: 60 },
  { label: '95/5', value: 95 },
];

const TOOLTIPS = {
  il: 'Impermanent Loss is the difference between holding tokens in a liquidity pool vs simply holding them in your wallet. It occurs when the price ratio of pooled tokens changes from the ratio at the time of deposit.',
  hodl: 'The value your tokens would have if you simply held them in your wallet instead of depositing into a liquidity pool.',
  lpValue: 'The value of your share in the liquidity pool, calculated by the AMM formula. This is always less than or equal to the HODL value when prices diverge.',
  netPnl: 'Your net profit or loss from providing liquidity, factoring in both the impermanent loss and the yield (fees/rewards) earned from the pool.',
  breakeven: 'The minimum APR the pool must earn in fees/rewards to offset the impermanent loss. If pool APR is above this number, LP is profitable.',
  weight: 'Pool weight determines the ratio of tokens. 50/50 is standard (Uniswap). Weighted pools like 80/20 (Balancer) reduce IL for the dominant token but increase it for the other.',
  priceRatio: 'The ratio of price change between the two tokens. IL depends only on relative price movement, not absolute prices.',
};

function pctToSlider(pct) {
  return ((pct + 90) / 590) * 100;
}
function sliderToPct(val) {
  return (val / 100) * 590 - 90;
}

function calcPriceFromPctChange(initial, pctChange) {
  return initial * (1 + pctChange / 100);
}

function calcPctChange(initial, future) {
  if (!initial || initial === 0) return 0;
  return ((future - initial) / initial) * 100;
}

/* --- IL Formulas --- */
function calcIL5050(priceRatio) {
  // IL = 2 * sqrt(r) / (1 + r) - 1
  return 2 * Math.sqrt(priceRatio) / (1 + priceRatio) - 1;
}

function calcILWeighted(priceRatio, weightA) {
  // For weighted pools: IL = r^w / (w*r + (1-w)) - 1
  // where w = weight of token A, r = price ratio of token A relative to B
  const w = weightA / 100;
  if (w === 0.5) return calcIL5050(priceRatio);
  const numerator = Math.pow(priceRatio, w);
  const denominator = w * priceRatio + (1 - w);
  return numerator / denominator - 1;
}

/* --- IL curve data for SVG chart --- */
function generateILCurve(weightA, numPoints = 200) {
  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    // price ratio from 0.05 to 10
    const ratio = 0.05 + (i / numPoints) * 9.95;
    const il = weightA === 50
      ? calcIL5050(ratio)
      : calcILWeighted(ratio, weightA);
    points.push({ ratio, il: il * 100 });
  }
  return points;
}

const bigNumberStyle = {
  fontFamily: "'DM Serif Display', serif",
  fontWeight: 400,
};

const monoStyle = {
  fontFamily: "'IBM Plex Mono', monospace",
};

/* --- Tooltip icon component --- */
function TooltipIcon({ text, colors }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 5, cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke={colors.labelMuted} strokeWidth="1.5" />
        <text x="8" y="12" textAnchor="middle" fontSize="10" fontWeight="600" fill={colors.labelMuted}>?</text>
      </svg>
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 8,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: '10px 14px',
          width: 260,
          ...monoStyle,
          fontSize: 11,
          lineHeight: 1.6,
          color: colors.textSecondary,
          boxShadow: `0 4px 16px ${colors.shadow}`,
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

export default function ILCalculatorView({ pools = [] }) {
  const { dark, colors } = useTheme();
  const isMobile = useIsMobile();

  const [tokenA, setTokenA] = useState('ETH');
  const [tokenB, setTokenB] = useState('USDC');
  const [priceA, setPriceA] = useState(2000);
  const [priceB, setPriceB] = useState(1);
  const [futurePriceA, setFuturePriceA] = useState(2000);
  const [futurePriceB, setFuturePriceB] = useState(1);
  const [investment, setInvestment] = useState(10000);
  const [manualApr, setManualApr] = useState(15);
  const [weightA, setWeightA] = useState(50);
  const [days, setDays] = useState(365);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [showCustomToken, setShowCustomToken] = useState({ a: false, b: false });
  const [customTokenA, setCustomTokenA] = useState('');
  const [customTokenB, setCustomTokenB] = useState('');

  const pctChangeA = calcPctChange(priceA, futurePriceA);
  const pctChangeB = calcPctChange(priceB, futurePriceB);

  const selectedPool = useMemo(() => {
    if (!selectedPoolId) return null;
    return pools.find(p => String(p.id) === String(selectedPoolId)) || null;
  }, [selectedPoolId, pools]);

  const effectiveApr = selectedPool ? selectedPool.apr : manualApr;
  const weightB = 100 - weightA;

  const displayTokenA = showCustomToken.a ? customTokenA || 'Token A' : tokenA;
  const displayTokenB = showCustomToken.b ? customTokenB || 'Token B' : tokenB;

  const results = useMemo(() => {
    const initA = Number(priceA) || 0;
    const initB = Number(priceB) || 0;
    const futA = Number(futurePriceA) || 0;
    const futB = Number(futurePriceB) || 0;
    const inv = Number(investment) || 0;
    const d = Number(days) || 365;
    const apr = Number(effectiveApr) || 0;
    const wA = Number(weightA) || 50;

    if (initA <= 0 || initB <= 0 || futA <= 0 || futB <= 0 || inv <= 0) {
      return { ilPct: 0, ilUsd: 0, hodlValue: 0, lpValue: 0, feeEarnings: 0, netResult: 0, breakEvenApr: 0, priceRatio: 1 };
    }

    const priceRatio = (futA / initA) / (futB / initB);
    const ilFactor = wA === 50 ? calcIL5050(priceRatio) : calcILWeighted(priceRatio, wA);
    const ilPct = ilFactor * 100;

    // For weighted pools: Token A gets wA% of investment, Token B gets wB%
    const wAFrac = wA / 100;
    const wBFrac = 1 - wAFrac;
    const tokensA = (inv * wAFrac) / initA;
    const tokensB = (inv * wBFrac) / initB;
    const hodlValue = tokensA * futA + tokensB * futB;

    const lpValue = hodlValue * (1 + ilFactor);
    const ilUsd = lpValue - hodlValue; // negative number

    // Time-adjusted fee earnings
    const timeFraction = d / 365;
    const feeEarnings = lpValue * (apr / 100) * timeFraction;
    const netResult = lpValue + feeEarnings - hodlValue;

    // Break-even APR: the APR needed so fee earnings = |IL|
    // feeEarnings = lpValue * (breakApr/100) * (d/365) = |ilUsd|
    const breakEvenApr = ilUsd !== 0 && lpValue > 0 && d > 0
      ? (Math.abs(ilUsd) / lpValue / timeFraction) * 100
      : 0;

    return { ilPct, ilUsd, hodlValue, lpValue, feeEarnings, netResult, breakEvenApr, priceRatio };
  }, [priceA, priceB, futurePriceA, futurePriceB, investment, effectiveApr, weightA, days]);

  const ilCurveData = useMemo(() => generateILCurve(weightA), [weightA]);

  const handleSliderA = useCallback((val) => {
    const pct = sliderToPct(Number(val));
    const newPrice = calcPriceFromPctChange(Number(priceA) || 1, pct);
    setFuturePriceA(Math.max(0.001, Number(newPrice.toFixed(4))));
  }, [priceA]);

  const handleSliderB = useCallback((val) => {
    const pct = sliderToPct(Number(val));
    const newPrice = calcPriceFromPctChange(Number(priceB) || 1, pct);
    setFuturePriceB(Math.max(0.001, Number(newPrice.toFixed(4))));
  }, [priceB]);

  const applyPreset = (preset) => {
    setTokenA(preset.tokenA);
    setTokenB(preset.tokenB);
    setPriceA(preset.priceA);
    setPriceB(preset.priceB);
    setFuturePriceA(preset.futureA);
    setFuturePriceB(preset.futureB);
    setWeightA(preset.weightA || 50);
    setSelectedPoolId('');
    setShowCustomToken({ a: false, b: false });
  };

  const handlePoolSelect = (poolId) => {
    setSelectedPoolId(poolId);
    if (poolId) {
      const pool = pools.find(p => String(p.id) === String(poolId));
      if (pool) {
        if (pool.tokens && pool.tokens[0]) setTokenA(pool.tokens[0]);
        if (pool.tokens && pool.tokens[1]) setTokenB(pool.tokens[1]);
        setShowCustomToken({ a: false, b: false });
      }
    }
  };

  const absIl = Math.abs(results.ilPct);
  const dangerLevel = absIl > 10 ? 'high' : absIl > 5 ? 'warning' : 'safe';
  const dangerColors = {
    safe: { color: colors.green, bg: colors.greenBg, border: colors.greenBorder, label: 'LOW RISK' },
    warning: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder, label: 'CAUTION' },
    high: { color: colors.red, bg: colors.redBg, border: colors.redBorder, label: 'HIGH IL' },
  };
  const danger = dangerColors[dangerLevel];

  const fmtUsd = (n) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : n > 0 ? '+' : '';
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    return `${sign}$${abs.toFixed(2)}`;
  };

  const fmtPct = (n) => {
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}%`;
  };

  const barMax = Math.max(results.hodlValue, results.lpValue, results.lpValue + results.feeEarnings, 1);

  // --- Styles ---
  const cardStyle = {
    background: colors.surface,
    borderRadius: 12,
    boxShadow: `0 1px 4px ${colors.shadow}`,
    padding: isMobile ? 16 : 24,
    border: `1px solid ${colors.border}`,
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
  };

  const labelStyle = {
    ...monoStyle,
    fontSize: 10,
    fontWeight: 600,
    color: colors.labelMuted,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  };

  const inputStyle = {
    ...monoStyle,
    fontSize: 13,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: '8px 12px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    background: colors.inputBg,
    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  };

  const sliderThumbCss = `
    input[type=range].il-slider {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 4px;
      border-radius: 2px;
      background: ${colors.border};
      outline: none;
    }
    input[type=range].il-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${colors.green};
      cursor: pointer;
      border: 2px solid ${colors.surface};
      box-shadow: 0 1px 4px ${colors.shadow};
    }
    input[type=range].il-slider::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${colors.green};
      cursor: pointer;
      border: 2px solid ${colors.surface};
      box-shadow: 0 1px 4px ${colors.shadow};
    }
  `;

  /* --- SVG IL Curve Chart --- */
  const chartWidth = isMobile ? 280 : 400;
  const chartHeight = 180;
  const chartPadding = { top: 20, right: 20, bottom: 30, left: 50 };
  const plotW = chartWidth - chartPadding.left - chartPadding.right;
  const plotH = chartHeight - chartPadding.top - chartPadding.bottom;

  const ilChart = useMemo(() => {
    const minIL = Math.min(...ilCurveData.map(p => p.il), -50);
    const maxIL = 0;
    const minR = 0.05;
    const maxR = 10;

    const xScale = (r) => chartPadding.left + ((Math.log(r) - Math.log(minR)) / (Math.log(maxR) - Math.log(minR))) * plotW;
    const yScale = (il) => chartPadding.top + ((maxIL - il) / (maxIL - minIL)) * plotH;

    // Build IL curve path
    const pathParts = ilCurveData.map((p, i) => {
      const x = xScale(p.ratio);
      const y = yScale(Math.max(p.il, minIL));
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const curvePath = pathParts.join(' ');

    // Current position
    const currentRatio = results.priceRatio;
    const currentIL = results.ilPct;
    const cx = xScale(Math.max(minR, Math.min(maxR, currentRatio)));
    const cy = yScale(Math.max(currentIL, minIL));

    // Break-even IL line (if APR provided)
    const apr = Number(effectiveApr) || 0;
    const timeFrac = (Number(days) || 365) / 365;
    // The IL% where yield exactly covers the loss
    // Net = 0 when feeEarnings = |ilUsd|, but IL is relative to hodl, so approximate
    // For display, we just show the IL% that the current APR can offset
    // breakEvenIL ~ -apr * timeFrac (rough approximation for visualization)
    const breakEvenIL = apr > 0 ? -apr * timeFrac * 0.5 : null;

    // Y-axis ticks
    const yTicks = [0, -10, -20, -30, -40, -50].filter(v => v >= minIL);
    // X-axis ticks (price ratios)
    const xTicks = [0.1, 0.25, 0.5, 1, 2, 4, 8].filter(v => v >= minR && v <= maxR);

    return { curvePath, cx, cy, currentRatio, currentIL, xScale, yScale, yTicks, xTicks, minIL, breakEvenIL };
  }, [ilCurveData, results.priceRatio, results.ilPct, effectiveApr, days, plotW, plotH, chartPadding.left, chartPadding.top]);

  const renderTokenSelector = (value, setValue, customValue, setCustomValue, showCustom, setShowCustom, label) => (
    <div>
      <label style={labelStyle}>{label}</label>
      {showCustom ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={customValue}
            onChange={e => setCustomValue(e.target.value.toUpperCase())}
            placeholder="Symbol"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={() => setShowCustom(false)}
            style={{
              ...monoStyle, fontSize: 10, background: 'none', border: `1px solid ${colors.border}`,
              borderRadius: 6, padding: '4px 8px', color: colors.labelMuted, cursor: 'pointer',
            }}
          >List</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={value} onChange={e => setValue(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
            {COMMON_TOKENS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button
            onClick={() => setShowCustom(true)}
            style={{
              ...monoStyle, fontSize: 10, background: 'none', border: `1px solid ${colors.border}`,
              borderRadius: 6, padding: '4px 8px', color: colors.labelMuted, cursor: 'pointer',
            }}
            title="Type a custom token name"
          >...</button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '20px 24px', maxWidth: 1060, margin: '0 auto' }}>
      <style>{sliderThumbCss}</style>

      {/* Banner Header */}
      <div style={{
        background: colors.bannerGradient,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
        padding: isMobile ? '18px 16px' : '22px 28px',
        marginBottom: 22,
      }}>
        <h2 style={{ ...bigNumberStyle, fontSize: isMobile ? 22 : 28, color: colors.textPrimary, margin: '0 0 8px 0' }}>
          Impermanent Loss Calculator
        </h2>
        <p style={{ ...monoStyle, fontSize: 12, color: colors.textSecondary, margin: 0, lineHeight: 1.7, maxWidth: 720 }}>
          When you provide liquidity to a DEX pool, the AMM rebalances your tokens as prices change.
          If one token rises or falls relative to the other, you end up with fewer of the winner and more of the loser
          compared to simply holding. This difference is called <strong style={{ color: colors.textPrimary }}>impermanent loss</strong>.
          It becomes "permanent" only when you withdraw. Use this tool to simulate scenarios and decide
          whether pool yields offset the risk.
        </p>
      </div>

      {/* Preset Scenarios */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {PRESETS.map(preset => (
          <button
            key={preset.label}
            onClick={() => applyPreset(preset)}
            style={{
              ...monoStyle,
              fontSize: 11,
              fontWeight: 500,
              color: colors.textSecondary,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 20,
              padding: '6px 14px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.hover; e.currentTarget.style.color = colors.textPrimary; }}
            onMouseLeave={e => { e.currentTarget.style.background = colors.surface; e.currentTarget.style.color = colors.textSecondary; }}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 20,
      }}>
        {/* ============ LEFT: Inputs ============ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pool selector (if pools available) */}
          {pools.length > 0 && (
            <div style={cardStyle}>
              <label style={labelStyle}>Select existing pool (optional)</label>
              <select
                value={selectedPoolId}
                onChange={e => handlePoolSelect(e.target.value)}
                style={selectStyle}
              >
                <option value="">-- Manual input --</option>
                {pools.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.protocol} / {p.chain}) - APR {typeof p.apr === 'number' ? p.apr.toFixed(1) : '?'}%
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Token pair + initial prices */}
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {renderTokenSelector(
                tokenA, setTokenA, customTokenA, setCustomTokenA,
                showCustomToken.a, (v) => setShowCustomToken(s => ({ ...s, a: v })),
                'Token A'
              )}
              {renderTokenSelector(
                tokenB, setTokenB, customTokenB, setCustomTokenB,
                showCustomToken.b, (v) => setShowCustomToken(s => ({ ...s, b: v })),
                'Token B'
              )}
              <div>
                <label style={labelStyle}>Initial Price {displayTokenA} (USD)</label>
                <input
                  type="number"
                  value={priceA}
                  onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); setPriceA(v); setFuturePriceA(Number(v) || 0); }}
                  style={inputStyle}
                  min="0"
                  step="any"
                />
              </div>
              <div>
                <label style={labelStyle}>Initial Price {displayTokenB} (USD)</label>
                <input
                  type="number"
                  value={priceB}
                  onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); setPriceB(v); setFuturePriceB(Number(v) || 0); }}
                  style={inputStyle}
                  min="0"
                  step="any"
                />
              </div>
            </div>
          </div>

          {/* Pool weight */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ ...labelStyle, margin: 0 }}>Pool Weight</label>
              <TooltipIcon text={TOOLTIPS.weight} colors={colors} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              {WEIGHT_PRESETS.map(wp => (
                <button
                  key={wp.label}
                  onClick={() => setWeightA(wp.value)}
                  style={{
                    ...monoStyle,
                    fontSize: 11,
                    fontWeight: weightA === wp.value ? 600 : 400,
                    color: weightA === wp.value ? colors.activeText : colors.textSecondary,
                    background: weightA === wp.value ? colors.activeBg : colors.surface,
                    border: `1px solid ${weightA === wp.value ? colors.activeBg : colors.border}`,
                    borderRadius: 6,
                    padding: '5px 12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {wp.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary, whiteSpace: 'nowrap' }}>{displayTokenA}</span>
              <input
                type="range"
                className="il-slider"
                min="5"
                max="95"
                step="1"
                value={weightA}
                onChange={e => setWeightA(Number(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary, whiteSpace: 'nowrap' }}>{displayTokenB}</span>
            </div>
            <div style={{ ...monoStyle, fontSize: 12, color: colors.textPrimary, textAlign: 'center', marginTop: 6, fontWeight: 600 }}>
              {weightA}/{weightB}
            </div>
          </div>

          {/* Investment, APR, Time period */}
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Investment (USD)</label>
                <input
                  type="number"
                  value={investment}
                  onChange={e => setInvestment(e.target.value === '' ? '' : Number(e.target.value))}
                  style={inputStyle}
                  min="0"
                  step="100"
                />
              </div>
              <div>
                <label style={labelStyle}>
                  {selectedPool ? 'Pool APR' : 'Est. APR (%)'}
                </label>
                {selectedPool ? (
                  <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: colors.green, fontWeight: 600, background: colors.greenBg, border: `1px solid ${colors.greenBorder}` }}>
                    {typeof selectedPool.apr === 'number' ? selectedPool.apr.toFixed(2) : '?'}%
                  </div>
                ) : (
                  <input
                    type="number"
                    value={manualApr}
                    onChange={e => setManualApr(e.target.value === '' ? '' : Number(e.target.value))}
                    style={inputStyle}
                    min="0"
                    step="0.1"
                  />
                )}
              </div>
              <div>
                <label style={labelStyle}>Time period (days)</label>
                <input
                  type="number"
                  value={days}
                  onChange={e => setDays(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))}
                  style={inputStyle}
                  min="1"
                  step="1"
                />
              </div>
            </div>
          </div>

          {/* Future prices with sliders */}
          <div style={cardStyle}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, margin: 0 }}>Simulated Price {displayTokenA} (USD)</label>
                <span style={{
                  ...monoStyle, fontSize: 11, fontWeight: 600,
                  color: pctChangeA >= 0 ? colors.green : colors.red,
                }}>
                  {pctChangeA >= 0 ? '\u2191' : '\u2193'} {fmtPct(pctChangeA)}
                </span>
              </div>
              <input
                type="number"
                value={futurePriceA}
                onChange={e => setFuturePriceA(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ ...inputStyle, marginBottom: 8 }}
                min="0"
                step="any"
              />
              <input
                type="range"
                className="il-slider"
                min="0" max="100" step="0.1"
                value={pctToSlider(pctChangeA)}
                onChange={e => handleSliderA(e.target.value)}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', ...monoStyle, fontSize: 9, color: colors.labelLight, marginTop: 2 }}>
                <span>-90%</span><span>0%</span><span>+500%</span>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, margin: 0 }}>Simulated Price {displayTokenB} (USD)</label>
                <span style={{
                  ...monoStyle, fontSize: 11, fontWeight: 600,
                  color: pctChangeB >= 0 ? colors.green : colors.red,
                }}>
                  {pctChangeB >= 0 ? '\u2191' : '\u2193'} {fmtPct(pctChangeB)}
                </span>
              </div>
              <input
                type="number"
                value={futurePriceB}
                onChange={e => setFuturePriceB(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ ...inputStyle, marginBottom: 8 }}
                min="0"
                step="any"
              />
              <input
                type="range"
                className="il-slider"
                min="0" max="100" step="0.1"
                value={pctToSlider(pctChangeB)}
                onChange={e => handleSliderB(e.target.value)}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', ...monoStyle, fontSize: 9, color: colors.labelLight, marginTop: 2 }}>
                <span>-90%</span><span>0%</span><span>+500%</span>
              </div>
            </div>
          </div>
        </div>

        {/* ============ RIGHT: Results ============ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* IL main result card */}
          <div style={{
            ...cardStyle,
            border: `1px solid ${danger.border}`,
            background: danger.bg,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ ...labelStyle, margin: 0, color: danger.color }}>Impermanent Loss</label>
                <TooltipIcon text={TOOLTIPS.il} colors={colors} />
              </div>
              <span style={{
                ...monoStyle, fontSize: 9, fontWeight: 600, color: danger.color,
                background: dark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
                border: `1px solid ${danger.border}`, borderRadius: 4, padding: '2px 8px', letterSpacing: 0.8,
              }}>
                {danger.label}
              </span>
            </div>
            <div style={{ ...bigNumberStyle, fontSize: isMobile ? 34 : 42, color: danger.color, lineHeight: 1 }}>
              {results.ilPct.toFixed(2)}%
            </div>
            <div style={{ ...monoStyle, fontSize: 14, color: danger.color, marginTop: 8, fontWeight: 500 }}>
              {fmtUsd(results.ilUsd)} USD on ${(Number(investment) || 0).toLocaleString()} invested
            </div>
            <div style={{ ...monoStyle, fontSize: 11, color: danger.color, marginTop: 4, opacity: 0.7 }}>
              Price ratio: {results.priceRatio.toFixed(4)}x &middot; Pool weight: {weightA}/{weightB}
            </div>
          </div>

          {/* Value comparison */}
          <div style={cardStyle}>
            <label style={labelStyle}>Value Comparison ({days} days)</label>

            {/* HODL */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>If you HODL</span>
                  <TooltipIcon text={TOOLTIPS.hodl} colors={colors} />
                </span>
                <span style={{ ...bigNumberStyle, fontSize: 20, color: colors.textPrimary }}>
                  {fmtUsd(results.hodlValue)}
                </span>
              </div>
              <div style={{ height: 8, background: colors.scoreTrack, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${(results.hodlValue / barMax) * 100}%`,
                  background: colors.textSecondary, borderRadius: 4, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>

            {/* LP value */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>LP Value (no fees)</span>
                  <TooltipIcon text={TOOLTIPS.lpValue} colors={colors} />
                </span>
                <span style={{ ...bigNumberStyle, fontSize: 20, color: results.lpValue >= results.hodlValue ? colors.green : colors.red }}>
                  {fmtUsd(results.lpValue)}
                </span>
              </div>
              <div style={{ height: 8, background: colors.scoreTrack, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${(results.lpValue / barMax) * 100}%`,
                  background: results.lpValue >= results.hodlValue ? colors.green : colors.red,
                  borderRadius: 4, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>

            {/* LP + Fees */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>LP + Fees ({days}d est.)</span>
                <span style={{ ...bigNumberStyle, fontSize: 20, color: (results.lpValue + results.feeEarnings) >= results.hodlValue ? colors.green : colors.red }}>
                  {fmtUsd(results.lpValue + results.feeEarnings)}
                </span>
              </div>
              <div style={{ height: 8, background: colors.scoreTrack, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${((results.lpValue + results.feeEarnings) / barMax) * 100}%`,
                  background: (results.lpValue + results.feeEarnings) >= results.hodlValue ? colors.green : colors.red,
                  borderRadius: 4, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          </div>

          {/* Detailed breakdown */}
          <div style={cardStyle}>
            <label style={labelStyle}>P&L Breakdown</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...monoStyle, fontSize: 12, color: colors.textSecondary }}>Yield earned ({days}d at {Number(effectiveApr).toFixed(1)}% APR)</span>
                <span style={{ ...bigNumberStyle, fontSize: 18, color: colors.green }}>
                  +{fmtUsd(results.feeEarnings)}
                </span>
              </div>
              <div style={{ height: 1, background: colors.borderLight }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...monoStyle, fontSize: 12, color: colors.textSecondary }}>IL Cost</span>
                <span style={{ ...bigNumberStyle, fontSize: 18, color: colors.red }}>
                  {fmtUsd(results.ilUsd)}
                </span>
              </div>
              <div style={{ height: 1, background: colors.borderLight }} />

              {/* Break-even APR */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ ...monoStyle, fontSize: 12, color: colors.textSecondary }}>Break-even APR</span>
                  <TooltipIcon text={TOOLTIPS.breakeven} colors={colors} />
                </span>
                <span style={{
                  ...bigNumberStyle, fontSize: 18,
                  color: Number(effectiveApr) >= results.breakEvenApr ? colors.green : colors.gold,
                }}>
                  {results.breakEvenApr.toFixed(2)}%
                </span>
              </div>
              <div style={{ height: 1, background: colors.borderLight }} />

              {/* Net P&L */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: results.netResult >= 0 ? colors.greenBg : colors.redBg,
                borderRadius: 8, padding: '10px 12px', marginTop: 4,
              }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    ...monoStyle, fontSize: 12, fontWeight: 600,
                    color: results.netResult >= 0 ? colors.green : colors.red,
                  }}>
                    Net P&L (LP vs HODL)
                  </span>
                  <TooltipIcon text={TOOLTIPS.netPnl} colors={colors} />
                </span>
                <span style={{
                  ...bigNumberStyle, fontSize: 22,
                  color: results.netResult >= 0 ? colors.green : colors.red,
                }}>
                  {fmtUsd(results.netResult)}
                </span>
              </div>
              <div style={{ ...monoStyle, fontSize: 10, color: colors.labelLight, textAlign: 'center', marginTop: 2 }}>
                {results.netResult >= 0
                  ? `Providing liquidity is profitable: yield covers IL over ${days} days`
                  : `HODL beats LP by ${fmtUsd(Math.abs(results.netResult))} in this scenario. Need >${results.breakEvenApr.toFixed(1)}% APR to break even.`}
              </div>
            </div>
          </div>

          {/* IL Curve Chart */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <label style={{ ...labelStyle, margin: 0 }}>IL Curve ({weightA}/{weightB} pool)</label>
              <TooltipIcon text={TOOLTIPS.priceRatio} colors={colors} />
            </div>
            <svg
              width="100%"
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              style={{ display: 'block', overflow: 'visible' }}
            >
              {/* Grid lines */}
              {ilChart.yTicks.map(tick => {
                const y = ilChart.yScale(tick);
                return (
                  <g key={`y-${tick}`}>
                    <line
                      x1={chartPadding.left} y1={y}
                      x2={chartWidth - chartPadding.right} y2={y}
                      stroke={colors.border} strokeWidth="0.5" strokeDasharray="3,3"
                    />
                    <text
                      x={chartPadding.left - 6} y={y + 3}
                      textAnchor="end"
                      fill={colors.labelMuted}
                      fontSize="9"
                      fontFamily="'IBM Plex Mono', monospace"
                    >
                      {tick}%
                    </text>
                  </g>
                );
              })}

              {/* X-axis ticks */}
              {ilChart.xTicks.map(tick => {
                const x = ilChart.xScale(tick);
                return (
                  <g key={`x-${tick}`}>
                    <line
                      x1={x} y1={chartPadding.top}
                      x2={x} y2={chartHeight - chartPadding.bottom}
                      stroke={colors.border} strokeWidth="0.5" strokeDasharray="3,3"
                    />
                    <text
                      x={x} y={chartHeight - chartPadding.bottom + 14}
                      textAnchor="middle"
                      fill={colors.labelMuted}
                      fontSize="9"
                      fontFamily="'IBM Plex Mono', monospace"
                    >
                      {tick}x
                    </text>
                  </g>
                );
              })}

              {/* Axis labels */}
              <text
                x={chartWidth / 2} y={chartHeight - 2}
                textAnchor="middle"
                fill={colors.labelMuted}
                fontSize="9"
                fontFamily="'IBM Plex Mono', monospace"
              >
                Price Ratio
              </text>

              {/* Break-even IL line (green zone / red zone boundary) */}
              {ilChart.breakEvenIL !== null && ilChart.breakEvenIL > ilChart.minIL && (
                <>
                  {/* Green zone: area between breakEvenIL and 0% */}
                  <rect
                    x={chartPadding.left}
                    y={ilChart.yScale(0)}
                    width={plotW}
                    height={ilChart.yScale(ilChart.breakEvenIL) - ilChart.yScale(0)}
                    fill={colors.green}
                    opacity="0.06"
                  />
                  {/* Red zone: area below breakEvenIL */}
                  <rect
                    x={chartPadding.left}
                    y={ilChart.yScale(ilChart.breakEvenIL)}
                    width={plotW}
                    height={ilChart.yScale(ilChart.minIL) - ilChart.yScale(ilChart.breakEvenIL)}
                    fill={colors.red}
                    opacity="0.06"
                  />
                  {/* Break-even line */}
                  <line
                    x1={chartPadding.left}
                    y1={ilChart.yScale(ilChart.breakEvenIL)}
                    x2={chartWidth - chartPadding.right}
                    y2={ilChart.yScale(ilChart.breakEvenIL)}
                    stroke={colors.gold}
                    strokeWidth="1"
                    strokeDasharray="4,3"
                  />
                  <text
                    x={chartWidth - chartPadding.right - 2}
                    y={ilChart.yScale(ilChart.breakEvenIL) - 4}
                    textAnchor="end"
                    fill={colors.gold}
                    fontSize="8"
                    fontFamily="'IBM Plex Mono', monospace"
                  >
                    yield covers IL
                  </text>
                </>
              )}

              {/* IL curve */}
              <path
                d={ilChart.curvePath}
                fill="none"
                stroke={colors.red}
                strokeWidth="2"
                opacity="0.8"
              />

              {/* Current position marker */}
              {results.priceRatio >= 0.05 && results.priceRatio <= 10 && (
                <>
                  {/* Vertical dotted line from point to x-axis */}
                  <line
                    x1={ilChart.cx} y1={ilChart.cy}
                    x2={ilChart.cx} y2={chartHeight - chartPadding.bottom}
                    stroke={danger.color} strokeWidth="1" strokeDasharray="2,2" opacity="0.5"
                  />
                  {/* Horizontal dotted line from point to y-axis */}
                  <line
                    x1={chartPadding.left} y1={ilChart.cy}
                    x2={ilChart.cx} y2={ilChart.cy}
                    stroke={danger.color} strokeWidth="1" strokeDasharray="2,2" opacity="0.5"
                  />
                  {/* The dot */}
                  <circle
                    cx={ilChart.cx} cy={ilChart.cy}
                    r="5"
                    fill={danger.color}
                    stroke={colors.surface}
                    strokeWidth="2"
                  />
                  {/* Label */}
                  <text
                    x={ilChart.cx + 8} y={ilChart.cy - 8}
                    fill={danger.color}
                    fontSize="9"
                    fontWeight="600"
                    fontFamily="'IBM Plex Mono', monospace"
                  >
                    {results.ilPct.toFixed(2)}%
                  </text>
                </>
              )}
            </svg>
            <div style={{
              display: 'flex', justifyContent: 'center', gap: 20, marginTop: 10, flexWrap: 'wrap',
            }}>
              {ilChart.breakEvenIL !== null && (
                <>
                  <span style={{ ...monoStyle, fontSize: 10, color: colors.green, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, background: colors.green, opacity: 0.2, borderRadius: 2, display: 'inline-block' }} />
                    Yield &gt; IL
                  </span>
                  <span style={{ ...monoStyle, fontSize: 10, color: colors.red, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, background: colors.red, opacity: 0.2, borderRadius: 2, display: 'inline-block' }} />
                    IL &gt; Yield
                  </span>
                </>
              )}
              <span style={{ ...monoStyle, fontSize: 10, color: danger.color, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, background: danger.color, borderRadius: '50%', display: 'inline-block' }} />
                Current
              </span>
            </div>
          </div>

          {/* Formula reference */}
          <div style={{ ...cardStyle, background: colors.surfaceAlt }}>
            <label style={labelStyle}>IL Formula Reference</label>
            <div style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary, lineHeight: 1.8 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: colors.labelMuted, fontSize: 9 }}>50/50 POOL (xy=k):</span><br />
                priceRatio = (newA / initA) / (newB / initB)<br />
                IL = 2 * sqrt(priceRatio) / (1 + priceRatio) - 1
              </div>
              <div>
                <span style={{ color: colors.labelMuted, fontSize: 9 }}>WEIGHTED POOL:</span><br />
                IL = priceRatio^w / (w * priceRatio + (1 - w)) - 1<br />
                <span style={{ color: colors.labelLight, fontSize: 10 }}>where w = weight of token A ({weightA}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
